/* =========================
   Locale-aware date helpers
   ========================= */

/** Best-effort locale detection; fallback to en-GB (UK) */
function detectLocale(preferred?: string): string | undefined {
  if (preferred) return preferred;
  if (typeof navigator !== 'undefined') {
    if (Array.isArray((navigator as any).languages) && (navigator as any).languages.length) {
      return (navigator as any).languages[0];
    }
    if ((navigator as any).language) return (navigator as any).language;
  }
  try {
    // lets the runtime decide (often matches OS/browser locale)
    const resolved = Intl.DateTimeFormat().resolvedOptions().locale;
    if (resolved) return resolved;
  } catch { /* noop */ }
  // Explicitly prefer UK ordering if nothing else is available
  return 'en-GB';
}

/** Parse a local ISO-like string (YYYY-MM-DDTHH:mm or :ss) to a Date in local time. */
export function parseLocal(isoLocal: string): Date {
  try {
    const [d, t = '00:00:00'] = String(isoLocal).split('T');
    const [y, m, day] = d.split('-').map(n => parseInt(n, 10));
    const [hh = 0, mm = 0, ss = 0] = t.split(':').map(n => parseInt(n, 10));
    return new Date(y, (m || 1) - 1, day || 1, hh || 0, mm || 0, ss || 0, 0);
  } catch {
    return new Date(isoLocal);
  }
}

/** Return YYYY-MM-DDTHH:mm (no seconds) for input Date. */
export function toLocalInputValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

/** Add minutes to a date (immutably). */
export function addMinutes(date: Date, minutes: number): Date {
  const d = new Date(date.getTime());
  d.setMinutes(d.getMinutes() + (minutes || 0));
  return d;
}

/** Ensure seconds are present in a local ISO (YYYY-MM-DDTHH:mm[:ss]). */
export function ensureSeconds(isoLocal: string): string {
  if (!isoLocal) return isoLocal;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(isoLocal) ? `${isoLocal}:00` : isoLocal;
}

/** Locale-aware one-liner like “6 Sept 2025, 18:00” in en-GB (browser decides). */
export function formatLocaleShort(isoLocal: string, locale?: string): string {
  const d = parseLocal(ensureSeconds(isoLocal));
  const lang = detectLocale(locale);
  // Let Intl compose date+time with the right local ordering/punctuation
  return new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

/** Human-friendly same-day range; locale ordering respected. */
export function formatFriendlyRange(startIsoLocal: string, endIsoLocal: string, locale?: string): string {
  const start = parseLocal(ensureSeconds(startIsoLocal));
  const end = parseLocal(ensureSeconds(endIsoLocal));
  const lang = detectLocale(locale);

  const datePart = new Intl.DateTimeFormat(lang, { dateStyle: 'medium' }).format(start);
  const timeFmt = new Intl.DateTimeFormat(lang, { timeStyle: 'short' });

  return `${datePart} • ${timeFmt.format(start)} – ${timeFmt.format(end)}`;
}

/* =========================
   Countdown (no “LIVE”)
   ========================= */

function pad2(n: number) { return n < 10 ? `0${n}` : String(n); }
function splitDelta(ms: number) {
  let s = Math.max(0, Math.floor(Math.abs(ms) / 1000));
  const days = Math.floor(s / 86400); s -= days * 86400;
  const hours = Math.floor(s / 3600); s -= hours * 3600;
  const minutes = Math.floor(s / 60); s -= minutes * 60;
  const seconds = s;
  return { days, hours, minutes, seconds };
}

/**
 * Live-friendly countdown text (updates each second) but ONLY counts to start.
 * - Upcoming: "3d 04h 12m 09s"
 * - Past:     "12m 05s ago"
 */
export function countdownText(startIsoLocal: string, _endIsoLocal?: string, now: Date = new Date()) {
  const start = parseLocal(ensureSeconds(startIsoLocal));

  if (now < start) {
    const d = splitDelta(start.getTime() - now.getTime());
    const text =
      d.days ? `${d.days}d ${pad2(d.hours)}h ${pad2(d.minutes)}m ${pad2(d.seconds)}s`
             : d.hours ? `${d.hours}h ${pad2(d.minutes)}m ${pad2(d.seconds)}s`
                       : `${d.minutes}m ${pad2(d.seconds)}s`;
    return { text, status: 'upcoming' as const };
  }

  const d = splitDelta(now.getTime() - start.getTime());
  const text =
    d.days ? `${d.days}d ${pad2(d.hours)}h ${pad2(d.minutes)}m ${pad2(d.seconds)}s ago`
           : d.hours ? `${d.hours}h ${pad2(d.minutes)}m ${pad2(d.seconds)}s ago`
                     : `${d.minutes}m ${pad2(d.seconds)}s ago`;
  return { text, status: 'past' as const };
}

/* =========================
   Proximity & Energy (to start only)
   ========================= */

export type Proximity = 'far' | 'approaching' | 'soon' | 'imminent' | 'live' | 'past';

/**
 * Bucket events by proximity to start (drives CSS color/energy).
 * Thresholds to start:
 *   ≥30d: far
 *   ≥7d:  approaching
 *   ≥1d:  soon
 *   <1d:  imminent
 *   past: past
 *
 * Back-compat signature:
 *   - proximityBucket(startIso, now)          // preferred (no live)
 *   - proximityBucket(startIso, endIso, now)  // accepted, but endIso ignored
 */
export function proximityBucket(
  startIsoLocal: string,
  endOrNow?: string | Date,
  maybeNow?: Date
): Proximity {
  const start = parseLocal(ensureSeconds(startIsoLocal));

  const now: Date = (endOrNow instanceof Date)
    ? endOrNow
    : (maybeNow instanceof Date ? maybeNow : new Date());

  if (now < start) {
    const days = (start.getTime() - now.getTime()) / 86400000;
    if (days >= 30) return 'far';
    if (days >= 7)  return 'approaching';
    if (days >= 1)  return 'soon';
    return 'imminent';
  }
  return 'past';
}

/**
 * Visual energy ramps from 0 → 1 over the final 7 days to start.
 *
 * Back-compat signature:
 *   - energyLevel(startIso, now)
 *   - energyLevel(startIso, endIso, now)  // endIso ignored
 */
export function energyLevel(
  startIsoLocal: string,
  endOrNow?: string | Date,
  maybeNow?: Date
): number {
  const start = parseLocal(ensureSeconds(startIsoLocal));
  const now: Date = (endOrNow instanceof Date)
    ? endOrNow
    : (maybeNow instanceof Date ? maybeNow : new Date());

  const ms = start.getTime() - now.getTime();
  if (ms <= 0) return 0;

  const daysLeft = ms / 86400000;
  // 7+ days out = 0; 0 days out = 1
  const e = 1 - Math.min(1, Math.max(0, daysLeft / 7));
  return Number.isFinite(e) ? e : 0;
}
